import * as React from 'react';
import clsx from 'clsx';
import type {VNode} from 'snabbdom';
import type {JsxVNodeChildren} from 'snabbdom-jsx-lite';

import type {StrictJsxProps} from 'common/util/jsx';
import {snabbdomToReact} from 'common/util/panelish/transformer';
import {panelChildrenToList, react2panel, withHostStyle} from 'common/util/react2panel';

import {ButtonReact} from './component';
import type {ButtonAttrs, ButtonJsxProps, ButtonProps} from './types';

/** The `ButtonReact` prop each Button attr sets. Any other attr is set on the button root as it is. */
const BUTTON_ATTR_PROPS = {
  disabled: `disabled`,
  elref: `elref`,
  'not-focusable': `notFocusable`,
  readonly: `readonly`,
  'style-override': `styleOverride`,
  'highlight-icon': `highlightIcon`,
  'fill-icon': `fillIcon`,
  icon: `icon`,
  'icon-color': `iconColor`,
  'icon-right': `iconRight`,
  size: `size`,
  theme: `theme`,
  type: `type`,
  pressed: `pressed`,
  tooltip: `tooltip`,
  layout: `layout`,
  'disable-pressed-styles': `disablePressedStyles`,
  'disabled-stays-opaque': `disabledStaysOpaque`,
  href: `href`,
  download: `download`,
  target: `target`,
  tabindex: `tabIndex`,
} as const satisfies Partial<Record<string, keyof ButtonProps>>;

type PanelListener = (event: Event) => void;

type PanelButtonProps = ButtonProps & {
  /** The caller's `on` listeners, attached to the button root as native listeners. */
  listeners: Record<string, PanelListener>;
};

/**
 * Attaches the listeners to the button root, which is the element Panel callers had them on
 * before: `focus` and `blur` don't bubble, and a click handler reads the root as `currentTarget`.
 */
function PanelButton({listeners, ...buttonProps}: PanelButtonProps) {
  const [rootElement, setRootElement] = React.useState<HTMLElement | null>(null);
  const latestListeners = React.useRef(listeners);
  React.useLayoutEffect(() => {
    latestListeners.current = listeners;
  });

  const eventNames = Object.keys(listeners).join(` `);
  React.useLayoutEffect(() => {
    if (!rootElement || !eventNames) {
      return undefined;
    }
    const rootListeners = eventNames
      .split(` `)
      .map((eventName): [string, PanelListener] => [eventName, (event) => latestListeners.current[eventName]?.(event)]);
    for (const [eventName, listener] of rootListeners) {
      rootElement.addEventListener(eventName, listener);
    }
    return () => {
      for (const [eventName, listener] of rootListeners) {
        rootElement.removeEventListener(eventName, listener);
      }
    };
  }, [rootElement, eventNames]);

  return <ButtonReact {...buttonProps} ref={setRootElement} />;
}

const PanelButtonHost = react2panel(`mp-button`, PanelButton, {syncMount: true});

const SEL_CLASS_PREFIX = `mp-button-sel-`;

/**
 * Panel-form factory for the button. The host element is `display: contents` and carries only the
 * caller's `slot`, `key` and `hook`, so the button root lays out as the caller's child. The root
 * takes everything else: the attrs, `params`, the `sel` and `class` classes, `style`, the `on`
 * listeners and the children.
 */
export function ButtonEl(props?: StrictJsxProps<ButtonJsxProps>, children?: JsxVNodeChildren): VNode {
  const {attrs = {}, params = {}, on = {}, sel, class: classes, style, key, hook} = props ?? {};
  const {slot, class: classAttribute, ...buttonAttrs} = attrs as ButtonAttrs & Record<string, unknown>;

  const buttonProps: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(buttonAttrs)) {
    // Panel leaves a null or false attr off the element, so the button falls back to its default.
    if (value !== null && value !== undefined && value !== false) {
      buttonProps[BUTTON_ATTR_PROPS[name as keyof typeof BUTTON_ATTR_PROPS] ?? name] = value;
    }
  }
  if (params.tooltip?.text) {
    buttonProps.tooltip = params.tooltip;
  }

  const listeners = Object.fromEntries(
    Object.entries(on).filter((entry): entry is [string, PanelListener] => typeof entry[1] === `function`),
  );
  const childNodes = panelChildrenToList(children ?? (props as {children?: JsxVNodeChildren} | undefined)?.children);
  const selClasses = sel?.split(`.`).slice(1) ?? [];
  // snabbdom patches a host in place only when its `sel` matches, so the host's `sel` carries the
  // caller's classes, prefixed so no caller rule or class lookup matches the host. A button with
  // different classes in the same position then gets a new host, and focus doesn't carry over to it.
  const hostSel = selClasses.map((className) => `.${SEL_CLASS_PREFIX}${className}`).join(``);

  const node = PanelButtonHost({
    ...(buttonProps as Omit<ButtonProps, `class`>),
    sel: hostSel || undefined,
    label: params.label,
    className: clsx(selClasses, classes, classAttribute),
    style: style as React.CSSProperties | undefined,
    listeners,
    children: childNodes.length ? snabbdomToReact(childNodes) : undefined,
    hostAttrs: {slot: slot as string | undefined},
    hook: withHostStyle(hook, () => ({display: `contents`})),
  });
  node.key = key;
  return node;
}
